/**
 * WebSocket message schemas for Textborne frontend
 */

import { z } from 'zod';

// ============================================
// Client Message Types
// ============================================

export const ClientMessageTypeSchema = z.enum([
  'join_world',
  'leave_world',
  'user_input',
  'ping',
  'request_state',
  'stop_generation',
  'load_older_messages',
]);

export type ClientMessageType = z.infer<typeof ClientMessageTypeSchema>;

// ============================================
// Server Message Types
// ============================================

export const ServerMessageTypeSchema = z.enum([
  'connected',
  'narrative_start',
  'narrative_chunk',
  'narrative_end',
  'narrative_refined',
  'narrative_complete',
  'pipeline_phase',
  'simulation_status',
  'state_update',
  'error',
  'pong',
  'world_state',
  'balance_update',
  'companion_state_update',
  'characters_updated',
  'scene_update_failed',
  'world_facts_updated',
  'recent_beats_updated',
  'chapter_change',
  'chapter_updated',
  'chapters_revised',
  'recall_fired',
  'message_added',
  'message_updated',
  'message_deleted',
  'message_confirmed',
  'generation_stopping',
  'turn_interrupted',
  'older_messages',
]);

export type ServerMessageType = z.infer<typeof ServerMessageTypeSchema>;

// ============================================
// Server Message Schema
// ============================================

export const ServerMessageSchema = z.object({
  type: ServerMessageTypeSchema,
  id: z.string().optional(),
  payload: z.record(z.string(), z.unknown()),
  timestamp: z.string().optional(),
});

export type ServerMessage = z.infer<typeof ServerMessageSchema>;

// ============================================
// World State Schema
// ============================================

import {
  WorldSchema,
  WorldSeedSchema,
  CharacterSchema,
  MessageSchema,
  ArcSummarySchema,
  ChapterSummarySchema,
  CompanionProfileSchema,
  CompanionStateSchema,
} from '../entities';

export const WorldStateSchema = z.object({
  // Legacy/primary shape used by the frontend app state (from REST getWorld())
  world: WorldSchema,
  seed: WorldSeedSchema.nullable(),
  currentArc: ArcSummarySchema.nullable(),
  currentChapter: ChapterSummarySchema.nullable(),
  activeCharacters: z.array(CharacterSchema),
  recentMessages: z.array(MessageSchema),

  // Optional fields used by websocket connect payloads / newer implementations
  worldId: z.string().optional(),
  initialized: z.boolean().optional(),
  creditsBalanceMicros: z.number().nullable().optional(),
  worldSeed: z.object({
    genre: z.string().nullable().optional(),
    tone: z.string().nullable().optional(),
    provider: z.string().nullable().optional(),
    model: z.string().nullable().optional(),
  }).nullable().optional(),
  companionProfile: CompanionProfileSchema.nullable().optional(),
  companionState: CompanionStateSchema.nullable().optional(),
});

export type WorldState = z.infer<typeof WorldStateSchema>;
